<template>
  <layouts></layouts>
  <student-breadcrumb :title="title" :text="text" :text1="text1" />
  <div class="page-content">
    <div class="container">
      <div class="row">
        <instructor-sidebar></instructor-sidebar>

        <div class="col-xl-9 col-lg-9">
          <div class="settings-widget card-info">
            <div class="settings-menu p-0">
              <div class="profile-heading">
                <h3>Quiz Management</h3>
                <p>Manage quizzes and questions</p>
              </div>

              <div class="checkout-form pb-0">
                <div class="row align-items-center mb-4">
                  <div class="col-lg-4">
                    <div class="form-group">
                      <label>Filter by Course</label>
                      <select
                        v-model="selectedCourseId"
                        class="form-select"
                        @change="fetchQuizzes"
                      >
                        <option :value="null">All Courses</option>
                        <option
                          v-for="course in courses"
                          :key="course.id"
                          :value="course.id"
                        >
                          {{ course.title }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-lg-8 text-end">
                    <router-link
                      to="/caduceus/quizzes/import"
                      class="btn btn-secondary me-2"
                    >
                      <i class="bx bx-upload me-1"></i>Import from Excel
                    </router-link>
                    <router-link
                      to="/caduceus/quizzes/create"
                      class="btn btn-primary"
                    >
                      <i class="bx bx-plus me-1"></i>New Quiz
                    </router-link>
                  </div>
                </div>

                <div v-if="loading" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>

                <div v-else-if="error" class="alert alert-danger" role="alert">
                  {{ error }}
                </div>

                <div v-else-if="quizzes.length === 0" class="text-center py-5">
                  <p class="text-muted">No quizzes found. Create your first quiz!</p>
                </div>

                <div v-else class="row">
                  <div
                    v-for="quiz in quizzes"
                    :key="quiz.id"
                    class="col-xxl-6 col-md-6 d-flex"
                  >
                    <div class="course-box flex-fill mb-4">
                      <div class="product">
                        <div class="product-content">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h3 class="title instructor-text mb-0">
                              {{ quiz.title }}
                            </h3>
                            <span class="badge bg-primary">
                              {{ quiz._count?.questions || 0 }} questions
                            </span>
                          </div>

                          <p class="text-muted mb-2" v-if="quiz.description">
                            {{ truncateText(quiz.description, 100) }}
                          </p>

                          <div class="course-info d-flex align-items-center mb-3">
                            <div class="rating-img d-flex align-items-center me-3">
                              <i class="bx bx-book-open me-1"></i>
                              <small>Course: {{ getCourseName(quiz.courseId) }}</small>
                            </div>
                            <div class="course-view d-flex align-items-center">
                              <i class="bx bx-calendar me-1"></i>
                              <small>{{ formatDate(quiz.createdAt) }}</small>
                            </div>
                          </div>

                          <div class="course-edit-btn d-flex align-items-center justify-content-between">
                            <div>
                              <router-link
                                :to="`/caduceus/quizzes/${quiz.id}/edit`"
                                class="btn btn-sm btn-outline-primary me-2"
                              >
                                <i class="bx bx-edit me-1"></i>Edit
                              </router-link>
                              <button
                                @click="handleDuplicate(quiz.id)"
                                class="btn btn-sm btn-outline-secondary me-2"
                              >
                                <i class="bx bx-copy me-1"></i>Duplicate
                              </button>
                            </div>
                            <button
                              @click="confirmDelete(quiz)"
                              class="btn btn-sm btn-outline-danger"
                            >
                              <i class="bx bx-trash me-1"></i>Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <layouts1></layouts1>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import QuizService from '@/services/quiz.service';
import ApiService from '@/services/ApiService';
import Swal from 'sweetalert2';

const title = ref('Quiz Management');
const text = ref('Home');
const text1 = ref('Quizzes');

const quizzes = ref([]);
const courses = ref([]);
const selectedCourseId = ref(null);
const loading = ref(false);
const error = ref(null);

const fetchCourses = async () => {
  try {
    const response = await ApiService.get('/courses/me');
    courses.value = response.data || [];
  } catch (err) {
    console.error('Error fetching courses:', err);
  }
};

const fetchQuizzes = async () => {
  if (!selectedCourseId.value) {
    quizzes.value = [];
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const response = await QuizService.listByCourse(selectedCourseId.value);
    quizzes.value = response.data || [];
  } catch (err) {
    error.value = 'Failed to load quizzes. Please try again.';
    console.error('Error fetching quizzes:', err);
  } finally {
    loading.value = false;
  }
};

const getCourseName = (courseId) => {
  const course = courses.value.find(c => c.id === courseId);
  return course?.title || 'Unknown';
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const handleDuplicate = async (quizId) => {
  const { value: newTitle } = await Swal.fire({
    title: 'Duplicate Quiz',
    input: 'text',
    inputLabel: 'Enter new quiz title',
    inputPlaceholder: 'Quiz title',
    showCancelButton: true,
    confirmButtonText: 'Duplicate',
    inputValidator: (value) => {
      if (!value) {
        return 'Please enter a title';
      }
    }
  });

  if (newTitle) {
    try {
      await QuizService.duplicate(quizId, newTitle);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Quiz duplicated successfully'
      });
      fetchQuizzes();
    } catch (err) {
      console.error('Error duplicating quiz:', err);
    }
  }
};

const confirmDelete = async (quiz) => {
  const result = await Swal.fire({
    title: 'Delete Quiz?',
    html: `Are you sure you want to delete <strong>${quiz.title}</strong>?<br><br>` +
          `<span style="color: #dc3545;">` +
          `<i class="bx bx-error-circle"></i> ` +
          `This action cannot be undone. All questions and student attempts will be deleted.` +
          `</span>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel'
  });

  if (result.isConfirmed) {
    await handleDelete(quiz.id);
  }
};

const handleDelete = async (quizId) => {
  try {
    await QuizService.delete(quizId);

    Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: 'Quiz deleted successfully',
      timer: 2000,
      showConfirmButton: false
    });

    fetchQuizzes();
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.response?.data?.message || 'Failed to delete quiz'
    });
  }
};

onMounted(() => {
  fetchCourses();
});
</script>

<style scoped>
.course-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.course-box:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.product-content {
  padding: 1.5rem;
}

.title.instructor-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.course-info small {
  font-size: 0.85rem;
  color: #6c757d;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

.btn-sm {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}
</style>
